import React from 'react';
import { Button, Tag } from 'antd';
import {
  CheckCircleFilled,
  LockFilled,
  ThunderboltFilled,
  ExperimentOutlined,
  AimOutlined,
  RedoOutlined,
  PlayCircleOutlined,
  StopOutlined,
  ReadOutlined,
  StarFilled,
  ClockCircleFilled,
} from '@ant-design/icons';

import {
  DevAsset,
  DevPlanActiveSession,
  DevPlanState,
  NodeStatus,
  PlanNode,
  TrackCode,
} from '../types';
import { ConvictionCoverageNote, isLowConvictionCoverage } from 'components';

import {
  TIER_LABEL,
  lastResultMeta,
  playCostFor,
  unlockCostFor,
  unlockedGameKeys,
} from '../helpers';

type Props = {
  node: PlanNode | null;
  coreSelected: boolean;
  state: DevPlanState;
  nodes: PlanNode[];
  status: NodeStatus | null;
  byCode: Record<string, PlanNode>;
  trackColor: Record<TrackCode, string>;
  devForcePass: boolean;
  enforceLocks: boolean;
  onSelectNode: (code: string) => void;
  onPickAsset: (asset: DevAsset) => void;
  onOpenRank: (
    node: PlanNode,
    opts?: { focus?: 'scenario' | 'ladder'; ladderKey?: string },
  ) => void;
  onForcePass: (node: PlanNode) => void;
  /** Assessment-backed nodes: start the solo PM session (optionally a
   *  specific scenario — no forced order in the coin economy). */
  onLaunchSolo?: (
    node: PlanNode,
    blueprintSlug?: string,
    recommendationItemId?: number,
  ) => void;
  /** Continue a session explicitly advertised by `/me` (never inferred locally). */
  onContinueSession?: (session: DevPlanActiveSession) => void;
  /** Buy the node with coins. */
  onPurchase?: (node: PlanNode) => void;
  launching?: boolean;
};

const NodeChip: React.FC<{
  node: PlanNode;
  color: string;
  onClick: () => void;
}> = ({ node, color, onClick }) => {
  const unlocked = node.rank > 0;
  return (
    <button
      type="button"
      className={`devplan-chip-node${unlocked ? ' is-met' : ''}`}
      style={{ ['--track-color' as string]: color }}
      onClick={onClick}
    >
      {unlocked ? <CheckCircleFilled /> : <LockFilled />}
      <span>{node.name}</span>
    </button>
  );
};

/** A play chip for a scenario/game asset (or a disabled "not built yet" card). */
const AssetChip: React.FC<{
  asset: DevAsset;
  label?: string;
  onClick: () => void;
  locked?: boolean;
}> = ({ asset, label, locked, onClick }) => {
  if (!asset.is_built) {
    return (
      <span
        className="devplan-chip-scen is-unbuilt"
        title="Not built yet"
        aria-disabled
      >
        <StopOutlined /> {asset.title} · not built yet
      </span>
    );
  }
  if (locked) {
    return (
      <span
        className="devplan-chip-scen is-locked"
        title="Unlock this node to play"
        aria-disabled
      >
        <LockFilled /> {asset.title}
      </span>
    );
  }
  return (
    <button
      type="button"
      className="devplan-chip-scen"
      onClick={onClick}
      title={asset.file_path}
    >
      {label === 'scenario' ? <PlayCircleOutlined /> : <ExperimentOutlined />}{' '}
      {asset.title}
    </button>
  );
};

const NodeDetailPanel: React.FC<Props> = ({
  node,
  coreSelected,
  state,
  nodes,
  status,
  byCode,
  trackColor,
  devForcePass,
  enforceLocks,
  onSelectNode,
  onPickAsset,
  onOpenRank,
  onForcePass,
  onLaunchSolo,
  onContinueSession,
  onPurchase,
  launching,
}) => {
  // Games are gated by the shared rule: a practice game is playable once ANY
  // node serving it is unlocked (same set the Games tab uses). Computed at the
  // top so the hook runs unconditionally, ahead of the early returns below.
  const unlockedGames = React.useMemo(
    () => unlockedGameKeys(state, enforceLocks),
    [state, enforceLocks],
  );

  /* ---- Core overview --------------------------------------------------- */
  if (coreSelected) {
    return (
      <div className="devplan-detail">
        <div className="devplan-detail__eyebrow">The center of the map</div>
        <h2 className="devplan-detail__title">Macro Mastery</h2>
        <div className="devplan-detail__subtitle">
          Holistic Macro Trading Professional
        </div>
        <p className="devplan-detail__desc">
          Everything grows from here. Invest skill points across four tracks —
          breadth across the macro asset classes, sized with conviction, managed
          with discipline, expressed with a quant&apos;s eye for convexity.
        </p>
        <div className="devplan-detail__section-label">Tracks</div>
        <div className="devplan-detail__tracks">
          {state.tracks.map(t => (
            <div
              key={t.code}
              className="devplan-detail__track"
              style={{ ['--track-color' as string]: t.color }}
            >
              <span className="devplan-detail__track-dot" />
              <div>
                <div className="devplan-detail__track-name">{t.name}</div>
                <div className="devplan-detail__track-blurb">{t.blurb}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  /* ---- Empty state ----------------------------------------------------- */
  if (!node || !status) {
    return (
      <div className="devplan-detail devplan-detail--empty">
        <AimOutlined className="devplan-detail__empty-icon" />
        <div className="devplan-detail__empty-title">Select a node</div>
        <p className="devplan-detail__empty-text">
          Click any skill to inspect it, trace its prerequisites, and invest
          skill points. Drag to pan, scroll to zoom.
        </p>
      </div>
    );
  }

  /* ---- Node detail ----------------------------------------------------- */
  const color = trackColor[node.track_code];
  const track = state.tracks.find(t => t.code === node.track_code);
  const cluster = state.clusters.find(
    c => c.track_code === node.track_code && c.code === node.group,
  );
  const dependents = nodes.filter(n => n.prereqs.includes(node.code));

  const assetByKey: Record<string, DevAsset> = {};
  state.assets.forEach(a => {
    assetByKey[a.key] = a;
  });
  const scenarioAsset = node.scenario_asset_key
    ? assetByKey[node.scenario_asset_key]
    : undefined;
  const blueprintLadder = node.detail?.rank_ladder;
  // Older curriculum rows also contain bare slugs or only a rank ladder.
  const blueprintRefs = (node.detail?.blueprints ?? []).map(bp =>
    typeof bp === 'string'
      ? { slug: bp, name: bp, difficulty: '', assets: 0, news: 0 }
      : bp,
  );
  const seenSlugs = new Set(blueprintRefs.map(bp => bp.slug));
  const poolSlugs = Object.values(node.detail?.rank_pools ?? {}).flatMap(
    value => (Array.isArray(value) ? value : [value]),
  );
  [...Object.values(blueprintLadder ?? {}), ...poolSlugs].forEach(slug => {
    if (!seenSlugs.has(slug)) {
      blueprintRefs.push({
        slug,
        name: slug,
        difficulty: '',
        assets: 0,
        news: 0,
      });
      seenSlugs.add(slug);
    }
  });
  const recommendation =
    node.recommended && state.recommendation?.mode === 'advisory'
      ? state.recommendation.items.find(
          item =>
            item.node_code === node.code ||
            item.node_code === node.internal_code,
        )
      : undefined;
  const recommendedBlueprint = recommendation
    ? blueprintRefs.find(bp => bp.slug === recommendation.blueprint_slug)
    : undefined;
  const recommendedSlug = recommendation?.blueprint_slug;
  const recommendationItemId = recommendation?.id;
  const recommendationAssigned =
    !!recommendedSlug &&
    Number.isInteger(recommendationItemId) &&
    Number(recommendationItemId) > 0 &&
    (!!recommendedBlueprint ||
      Object.values(blueprintLadder ?? {}).includes(recommendedSlug));
  const activeSessions = (state.active_sessions ?? []).filter(
    session =>
      session.node_code === node.code ||
      session.node_code === node.internal_code,
  );
  const activeSession = activeSessions[0];
  const bestByKey = new Map((state.bests ?? []).map(b => [b.key, b]));
  const lastByKey = new Map((state.last_results ?? []).map(r => [r.key, r]));
  const games = state.node_games
    .filter(g => g.node_code === node.code)
    .map(g => ({ role: g.role, asset: assetByKey[g.asset_key] }))
    .filter((g): g is { role: typeof g.role; asset: DevAsset } => !!g.asset);

  const StatusIcon = status.unlocked
    ? CheckCircleFilled
    : status.canUnlock
      ? ThunderboltFilled
      : LockFilled;

  // Coin economy (2026-08-20): OWNERSHIP is the one gate. An unowned node
  // shows its price; an owned one plays — every play costs coins, and a
  // maxed node keeps playing for the personal-record bonus. The dev
  // force-pass keeps its free traversal role.
  const coins = state.wallet?.coins ?? 0;
  const nodeCost = unlockCostFor(node, state);
  const htmlPlayCost = playCostFor(node, state);
  const owned = status.unlocked || !enforceLocks;
  const scenarioLocked = !owned;
  let action: React.ReactNode;
  if (activeSession && onContinueSession) {
    action = (
      <Button
        block
        type="primary"
        icon={<ClockCircleFilled />}
        onClick={() => onContinueSession(activeSession)}
      >
        Continue {activeSession.blueprint_name || 'scenario'}
      </Button>
    );
  } else if (!owned) {
    action = (
      <Button
        block
        type="primary"
        icon={<LockFilled />}
        disabled={!status.canUnlock || !onPurchase}
        onClick={() => onPurchase && onPurchase(node)}
      >
        {status.canUnlock
          ? `Unlock — ${nodeCost} coins`
          : `Unlock — ${nodeCost} coins (you have ${coins})`}
      </Button>
    );
  } else if (recommendedSlug && onLaunchSolo) {
    const cost = recommendedBlueprint?.difficulty
      ? playCostFor(node, state, recommendedBlueprint.difficulty)
      : undefined;
    const affordable = cost === undefined || coins >= cost;
    action = (
      <Button
        block
        type="primary"
        icon={<PlayCircleOutlined />}
        loading={launching}
        disabled={!recommendationAssigned || (!affordable && enforceLocks)}
        onClick={() =>
          onLaunchSolo(node, recommendedSlug, recommendationItemId)
        }
      >
        {!recommendationAssigned
          ? 'Recommended scenario unavailable — refresh the plan'
          : `Start recommended scenario${cost === undefined ? '' : ` — ${cost} coins`}`}
      </Button>
    );
  } else if (scenarioAsset) {
    const affordable = coins >= htmlPlayCost;
    action = (
      <Button
        block
        type="primary"
        icon={<PlayCircleOutlined />}
        disabled={!affordable && enforceLocks}
        onClick={() => onOpenRank(node)}
      >
        {status.maxed
          ? `Beat your record — ${htmlPlayCost} coins`
          : `Play — ${htmlPlayCost} coins`}
      </Button>
    );
  } else if (blueprintRefs.length > 0 && onLaunchSolo) {
    action = (
      <Button
        block
        type="primary"
        icon={<PlayCircleOutlined />}
        loading={launching}
        onClick={() => onLaunchSolo(node)}
      >
        {status.maxed ? 'Play — beat your record' : 'Start next scenario'}
      </Button>
    );
  } else {
    action = (
      <Button block disabled icon={<LockFilled />}>
        No scenario
      </Button>
    );
  }

  return (
    <div
      className="devplan-detail"
      style={{ ['--track-color' as string]: color }}
    >
      <div className="devplan-detail__tags">
        <Tag color={color}>{track?.name}</Tag>
        <Tag>{TIER_LABEL[node.tier]}</Tag>
        {node.meter_name && <Tag>{node.meter_name}</Tag>}
        {node.recommended && (
          <Tag color="gold" icon={<StarFilled />}>
            Recommended
          </Tag>
        )}
        {activeSession && (
          <Tag color="blue" icon={<ClockCircleFilled />}>
            In progress
          </Tag>
        )}
        <span className="devplan-detail__code">{node.code}</span>
      </div>

      {node.recommended && (
        <div className="devplan-detail__reco">
          {recommendedSlug && (
            <div>
              <b>
                Suggested scenario:{' '}
                {recommendedBlueprint?.name || recommendedSlug}
              </b>
            </div>
          )}
          {recommendation?.rationale ||
            'Suggested for your next practice session.'}
          <div>
            {owned
              ? 'This node is owned. Play costs still apply.'
              : 'Unlock this node before playing. Play costs also apply.'}
          </div>
        </div>
      )}

      <h2 className="devplan-detail__title">{node.name}</h2>
      {cluster && (
        <div className="devplan-detail__subtitle">{cluster.name}</div>
      )}

      <div
        className={`devplan-detail__status devplan-status--${
          status.unlocked
            ? 'unlocked'
            : status.canUnlock
              ? 'available'
              : 'locked'
        }`}
      >
        <StatusIcon />
        <span>{status.reason}</span>
      </div>

      <div className="devplan-detail__rank">
        <span className="devplan-detail__section-label">Rank</span>
        <span className="devplan-detail__pips">
          {Array.from({ length: node.max_rank }).map((_, i) => (
            <span
              key={i}
              className={
                i < node.rank ? 'devplan-pip is-filled' : 'devplan-pip'
              }
            />
          ))}
        </span>
        <span className="devplan-detail__rank-num">
          {node.rank} / {node.max_rank}
        </span>
      </div>

      <p className="devplan-detail__desc">{node.description}</p>

      <div className="devplan-detail__section-label">
        Prerequisites
        {node.prereqs.length === 0 && (
          <span className="devplan-detail__none">
            {' '}
            — none (grows from core)
          </span>
        )}
      </div>
      {node.prereqs.length > 0 && (
        <div className="devplan-detail__chips">
          {node.prereqs.map(p => {
            const pre = byCode[p];
            if (!pre) return null;
            return (
              <NodeChip
                key={p}
                node={pre}
                color={trackColor[pre.track_code]}
                onClick={() => onSelectNode(p)}
              />
            );
          })}
        </div>
      )}

      {dependents.length > 0 && (
        <>
          <div className="devplan-detail__section-label">Unlocks</div>
          <div className="devplan-detail__chips">
            {dependents.map(d => (
              <NodeChip
                key={d.code}
                node={d}
                color={trackColor[d.track_code]}
                onClick={() => onSelectNode(d.code)}
              />
            ))}
          </div>
        </>
      )}

      {games.length > 0 && (
        <>
          <div className="devplan-detail__section-label">Practice games</div>
          <div className="devplan-detail__chips">
            {games.map(g => (
              <AssetChip
                key={g.asset.key}
                asset={g.asset}
                locked={!unlockedGames.has(g.asset.key)}
                onClick={() => onPickAsset(g.asset)}
              />
            ))}
          </div>
        </>
      )}

      {node.ladders.length > 0 && (
        <>
          <div className="devplan-detail__section-label">
            Warm-ups
            <span className="devplan-detail__none">
              {' '}
              — short prep, played with the rank
            </span>
          </div>
          <div className="devplan-detail__chips">
            {node.ladders.map(l => {
              const a = assetByKey[l.asset_key];
              if (!a) return null;
              if (!a.is_built) {
                return (
                  <span
                    key={l.asset_key}
                    className="devplan-chip-scen is-unbuilt"
                    aria-disabled
                  >
                    <StopOutlined /> Rank {l.rank} · not built yet
                  </span>
                );
              }
              if (scenarioLocked) {
                return (
                  <span
                    key={l.asset_key}
                    className="devplan-chip-scen is-locked"
                    title="Unlock this node to play"
                    aria-disabled
                  >
                    <LockFilled /> Rank {l.rank} warm-up
                  </span>
                );
              }
              const recommended = l.rank === node.rank + 1 && !l.done;
              return (
                <button
                  key={l.asset_key}
                  type="button"
                  className={`devplan-chip-scen devplan-chip-ladder${
                    recommended ? ' is-recommended' : ''
                  }`}
                  onClick={() =>
                    onOpenRank(node, { focus: 'ladder', ladderKey: a.key })
                  }
                  title={a.title}
                >
                  <ReadOutlined /> <b>Rank {l.rank}</b> warm-up
                  {l.done ? ' ✓' : ''}
                </button>
              );
            })}
          </div>
        </>
      )}

      {blueprintRefs.length > 0 && (
        <>
          <div className="devplan-detail__section-label">
            Scenarios
            <span className="devplan-detail__none">
              {' '}
              — play any you can afford
            </span>
          </div>
          <div className="devplan-scenlist">
            {blueprintRefs.map(bp => {
              const cost = bp.difficulty
                ? playCostFor(node, state, bp.difficulty)
                : undefined;
              const record = bestByKey.get(bp.slug);
              const last = lastByKey.get(bp.slug);
              // The trainee is the one person who can act on a score that
              // stood on few conviction views: say so under the row.
              const lowCoverage =
                last?.score_pct != null &&
                isLowConvictionCoverage(last.conviction_coverage);
              const scenarioSession = activeSessions.find(
                session => session.blueprint_slug === bp.slug,
              );
              const affordable = cost === undefined || coins >= cost;
              const playable = scenarioSession
                ? !!onContinueSession
                : owned &&
                  !!onLaunchSolo &&
                  (affordable || !enforceLocks) &&
                  (bp.slug !== recommendedSlug ||
                    (Number.isInteger(recommendationItemId) &&
                      Number(recommendationItemId) > 0));
              return (
                <React.Fragment key={bp.slug}>
                  <button
                    type="button"
                    className={`devplan-scenrow${
                      record?.passed ? ' is-passed' : ''
                    }${scenarioSession ? ' is-in-progress' : ''}${bp.slug === recommendedSlug ? ' is-recommended' : ''}`}
                    aria-label={
                      bp.slug === recommendedSlug
                        ? `Recommended scenario: ${bp.name}`
                        : undefined
                    }
                    disabled={!playable}
                    title={
                      scenarioSession
                        ? `Continue ${bp.name}`
                        : !owned
                          ? 'Unlock this node first'
                          : affordable
                            ? cost === undefined
                              ? 'Play — price checked at launch'
                              : `Play — ${cost} coins`
                            : `Costs ${cost} coins — you have ${coins}`
                    }
                    onClick={() => {
                      if (scenarioSession && onContinueSession) {
                        onContinueSession(scenarioSession);
                      } else if (playable && onLaunchSolo) {
                        if (bp.slug === recommendedSlug) {
                          onLaunchSolo(node, bp.slug, recommendationItemId);
                        } else {
                          onLaunchSolo(node, bp.slug);
                        }
                      }
                    }}
                  >
                    {scenarioSession ? (
                      <ClockCircleFilled />
                    ) : record?.passed ? (
                      <RedoOutlined />
                    ) : (
                      <PlayCircleOutlined />
                    )}
                    <span className="devplan-scenrow__name">
                      {bp.name}
                      {bp.slug === recommendedSlug && (
                        <Tag color="gold">Recommended</Tag>
                      )}
                    </span>
                    {scenarioSession ? (
                      <span className="devplan-scenrow__progress">
                        <b>In progress</b>
                        <span>Continue</span>
                      </span>
                    ) : (
                      <span className="devplan-scenrow__meta">
                        {cost === undefined
                          ? 'Price checked at launch'
                          : `${bp.difficulty} · ${cost}c`}
                        {record?.passed
                          ? ` · best ${Math.round(record.best_score)}`
                          : ''}
                        {lastResultMeta(last)}
                      </span>
                    )}
                  </button>
                  {lowCoverage && !scenarioSession && (
                    <ConvictionCoverageNote
                      coverage={last?.conviction_coverage}
                      className="devplan-scenrow__note"
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </>
      )}

      {scenarioAsset && (
        <>
          <div className="devplan-detail__section-label">Scenario</div>
          <div className="devplan-detail__chips">
            <AssetChip
              asset={scenarioAsset}
              label="scenario"
              locked={scenarioLocked}
              onClick={() => onOpenRank(node)}
            />
          </div>
        </>
      )}

      <div className="devplan-detail__action">
        {action}
        {devForcePass && !status.maxed && (
          <Button block danger onClick={() => onForcePass(node)}>
            Dev · force pass → rank {node.rank + 1}
          </Button>
        )}
        {status.maxed && scenarioAsset && (
          <Button
            block
            icon={<RedoOutlined />}
            onClick={() => onOpenRank(node)}
          >
            Replay scenario
          </Button>
        )}
      </div>
    </div>
  );
};

export default NodeDetailPanel;
